import axios from 'axios';
import BaseService from "./BaseService";
import UserTypesService from "./UserTypesService";
import UsersService from "./UsersService";
import settings from "../settings/settings";
import LogUtils from "../utils/LogUtils";
import MsgUtils from "../utils/MsgUtils";

class AuthService extends BaseService {

  constructor() {
    super();
    this.TAG = "AuthService";
    this.KEY_CURRENT_USER = "vc_ag_ag_current_user";
    const msgUtils = new MsgUtils();

    this.logUtils = new LogUtils();
    this.msgs = msgUtils.getMsgs();
    this.usersService = new UsersService();
    this.userTypeServices = new UserTypesService();
  }

  async updateIsAuthenticatedStatus() {
    try {
      const response = await axios(this.getBasicHttpRequest(
        this.HTTP_METHODS.GET,
        `${settings.AUTH_SERVICE_URL}/is-valid-token`,
        this.getDefaultHeaders(true)
      ));

      if (response?.data?.user?.id !== this.getCurrentUser()?.id) {
        this.setTkn(null);
        return false;
      }

    } catch (err) {
      this.setTkn(null);
      return false;
    }

    return true;
  }

  setCurrentUser(user) {
    try {
      localStorage.setItem(this.KEY_CURRENT_USER, JSON.stringify(user));

    } catch (err) {
      this.logUtils.logErrorMsg(this.TAG, "Failed to updateCurrentUser", err);
    }
  }

  getCurrentUser() {
    try {
      return JSON.parse(localStorage.getItem(this.KEY_CURRENT_USER));

    } catch (err) {
      this.logUtils.logErrorMsg(this.TAG, "Failed to getCurrentUser", err);
    }

    return null;
  }

  async login(email, password) {
    let response = null;
    let currentUser = null;

    try {
      // Login on the Auth MS
      response = await axios(this.getBasicHttpRequest(
        this.HTTP_METHODS.POST,
        `${settings.AUTH_SERVICE_URL}/login`,
        this.getDefaultHeaders(false), {
        'email': email,
        'password': password
      }
      ));

      if (!response?.data?.token) {
        return {
          status: 0,
          msg: this.msgs.INVALID_ACCESS_CREDENTIALS
        };
      }

      // Try to get the currentUser from the Management MS
      this.setTkn(response.data.token);
      const opGetCurrentUser = await this.usersService.getUserByEmail(email);

      if (!opGetCurrentUser.data) {
        return {
          status: -1,
          msg: this.msgs.UNAUTHORIZED_USER
        };
      }

      // Confirm userType
      if (!this.isAllowedUserType(opGetCurrentUser.data.user)) {
        return {
          status: -2,
          msg: this.msgs.UNAUTHORIZED_USER_TYPE
        };
      }

      this.setTkn(response.data.token);
      currentUser = opGetCurrentUser.data;
      this.setCurrentUser(currentUser);

    } catch (err) {
      this.logUtils.logErrorMsg(this.TAG, "Failed to login", err);

      return {
        status: -3,
        msg: "Não foi possível fazer o login"
      };
    }

    this.logUtils.logMsg(this.TAG, this.msgs.SUCCESSFULLY_LOGGED_IN);

    return {
      status: 1,
      msg: this.msgs.SUCCESSFULLY_LOGGED_IN,
      data: currentUser
    };
  };

  async refreshUser(email) {
    let currentUser = null;

    try {
      const opGetCurrentUser = await this.usersService.getUserByEmail(email);

      if (!opGetCurrentUser.data) {
        return {
          status: -1,
          msg: this.msgs.UNAUTHORIZED_USER
        };
      }

      if (!this.isAllowedUserType(opGetCurrentUser?.data?.user)) {
        return {
          status: -2,
          msg: this.msgs.UNAUTHORIZED_USER_TYPE
        };
      }

      currentUser = opGetCurrentUser?.data;
      this.setCurrentUser(currentUser);

    } catch (err) {
      this.logUtils.logErrorMsg(this.TAG, "Failed to refreshUser", err);

      return {
        status: -3,
        msg: "Não foi possível fazer o refreshUser"
      };
    }

    return {
      status: 1,
      msg: "Usuário renovado com sucesso",
      data: currentUser
    };
  };

  isAllowedUserType(user) {
    try {

      if (!user?.user_type?.name) {
        return false;
      }

      return Object.keys(this.userTypeServices.ALLOWED_USER_TYPES).includes(user.user_type.name);

    } catch (err) {
      this.logUtils.logErrorMsg(this.TAG, "Failed on isAllowedUserType", err);
    }

    return false;
  }

  async logout() {
    try {
      const response = await axios(this.getBasicHttpRequest(
        this.HTTP_METHODS.POST,
        `${settings.AUTH_SERVICE_URL}/logout`,
        this.getDefaultHeaders(true)
      ));

      if (response.data.op_status !== 1) {
        return {
          status: 0,
          msg: this.msgs.UNABLED_TO_LOGOUT
        };
      }

      this.setCurrentUser(null);
      this.setTkn(null);

    } catch (err) {
      this.logUtils.logErrorMsg(this.TAG, "Failed to logout", err);

      return {
        status: -1,
        msg: this.msgs.UNABLED_TO_LOGOUT
      };
    }

    this.logUtils.logMsg(this.TAG, this.msgs.SUCCESSFULLY_LOGGED_OUT);

    return {
      status: 1,
      msg: this.msgs.SUCCESSFULLY_LOGGED_OUT
    };
  }

  async sendInstructionsToRecoverPassword(email) {
    try {
      const response = await axios(this.getBasicHttpRequest(
        this.HTTP_METHODS.POST,
        `${settings.AUTH_SERVICE_URL}/forgot-pass`,
        this.getDefaultHeaders(false), {
        'email': email
      }
      ));

      if (response.data.op_status === 0) {
        return {
          status: 0,
          msg: this.msgs.INVALID_EMAIL_USER_NOT_FOUND
        };
      }

    } catch (err) {
      this.logUtils.logErrorMsg(this.TAG, "Failed to sendInstructionsToRecoverPassword", err);

      return {
        status: -1,
        msg: this.msgs.UNABLE_SEND_INSTRUCTIONS
      };
    }

    this.logUtils.logMsg(this.TAG, this.msgs.SUCCESSFULLY_SENT_INSTRUCTIONS_RECOVER_PASSWORD);

    return {
      status: 1,
      msg: this.msgs.SUCCESSFULLY_SENT_INSTRUCTIONS_RECOVER_PASSWORD
    };
  };

  async resetPassword(email, code, newPassword) {
    try {
      const response = await axios(this.getBasicHttpRequest(
        this.HTTP_METHODS.POST,
        `${settings.AUTH_SERVICE_URL}/reset-pass`,
        this.getDefaultHeaders(false), {
          'email': email,
          'token': code,
          'password': newPassword
        }
      ));

      if (response.data.op_status === 0) {
        return {
          status: 0,
          msg: this.msgs.INVALID_EMAIL_USER_NOT_FOUND
        };
      }

      if (response.data.op_status !== 1) {
        return {
          status: -1,
          msg: this.msgs.INVALID_ACCESS_CREDENTIALS
        };
      }

    } catch (err) {
      this.logUtils.logErrorMsg(this.TAG, "Failed to resetPassword", err);

      return {
        status: -2,
        msg: this.msgs.UNABLE_RESET_PASSWORD
      };
    }

    this.logUtils.logMsg(this.TAG, this.msgs.SUCCESSFULLY_UPDATED_PASSWORD);

    return {
      status: 1,
      msg: this.msgs.SUCCESSFULLY_UPDATED_PASSWORD
    };
  };

}

export default AuthService;

export function isAuthenticated() {
  const authService = new AuthService();

  try {
    return typeof authService.getTkn() === 'string' && authService.getTkn() !== "";

  } catch (err) {
    authService.logUtils.logErrorMsg(authService.TAG, "Failed on isAuthenticated", err);
  }

  return false;
}
